import { fail, success } from '@/helpers/result-helper.js'
import router from '@router'
import firebase from '@/plugins/firebase'
import toast from '@/services/toasts/index.js'
import msal from '@plugins/msal'
import $date from '@/services/date/index.js'
import {
  isCacheFresh,
  getSavedState,
  saveState,
  deleteState,
} from '@/helpers/cache-helpers'
import { DurationUnits } from '@/shared/constants/date/DurationUnits.js'
import { isSuccess } from '@/helpers/http-status-helpers'
import { LinkType } from '@/shared/constants/permissions/LinkType'
import {
  processPermissionsPayload,
  getNodeById,
  getNodePermission,
  hasPermissionAtAnyLevel,
  iterateOverPermissionsTree,
  flattenAccessTree,
  flattenPermissionsWithLocations,
} from '@/helpers/permissions-helpers'
import { PermissionModifier } from '@/shared/constants/permissions/PermissionModifier'
import { PermissionScope } from '@/shared/constants/permissions/PermissionScope'
import { OperationReturnType } from '@/shared/constants/permissions/OperationReturnType'
import { Tags } from '@/shared/constants/permissions/Tags.js'
import config from '@common/config.js'
import { getLanguageBasedOnBaseURL } from '@/helpers/language-helpers'
import ErrorResponseFactory from '@/services/error/ErrorResponseFactory'
import StoreErrorDTO from '@/models/error/storeErrorDTO'
import { isNonEmptyArray } from '@/helpers/array-helpers'

export default {
  namespaced: true,
  state: {
    // MSAL User
    account: getSavedState('auth.account'),
    interactionRequired: true,
    // User Profile from DB
    currentUser: getSavedState('auth.currentUser'),
    accessToken: '', // Bearer token
    lastTokenRefresh: null,
    loadingCount: 0,
    auth: firebase,
    permissions: [],
    generalFiles: [],
    impersonateContactId: getSavedState('auth.impersonateContactId'),
    username: null, // used to track errors when user profile is not set
  },

  mutations: {
    SET_CURRENT_USER(state, newValue) {
      state.currentUser = { ...newValue, ...{ lastUpdated: $date() } }
      saveState('auth.currentUser', state.currentUser)
    },
    SET_ACCOUNT(state, newValue) {
      state.account = newValue
      saveState('auth.account', newValue)
    },
    SET_IMPERSONATE_CONTACT_ID(state, contactId) {
      state.impersonateContactId = contactId
      saveState('auth.impersonateContactId', contactId)
    },
    SET_USER_PERMISSIONS(state, permissions) {
      state.permissions = processPermissionsPayload(permissions)
    },
    SET_USER_GENERALFILES(state, files) {
      state.generalFiles = files
    },
    SET_INTERACTION_REQUIRED(state, newValue) {
      state.interactionRequired = newValue
    },
    SET_ACCESS_TOKEN(state, token) {
      state.accessToken = token
      state.interactionRequired = false
      state.lastTokenRefresh = $date()
    },
    SET_USER_TO_UNAUTHENTICATED(state) {
      state.account = null
      state.interactionRequired = true
      state.currentUser = null
      state.impersonateContactId = null
      deleteState('auth.account')
      deleteState('auth.currentUser')
      deleteState('auth.impersonateContactId')
      deleteState('client.id')
      deleteState('client.name')
      state.accessToken = null
      state.username = null

      sessionStorage.clear()
      localStorage.clear()
    },
    FRESH_IMPERSONATE_CLEAR_STORE(state) {
      state.currentUser = null
      state.impersonateContactId = null
      state.permission = []
      deleteState('auth.currentUser')
      deleteState('auth.impersonateContactId')
      deleteState('client.id')
      deleteState('client.name')
    },
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    SET_USERNAME(state, username) {
      state.username = username
    },
  },

  getters: {
    moduleName: () => 'auth',
    currentUserFullName: (state) =>
      state.currentUser
        ? `${state.currentUser.firstName} ${state.currentUser.lastName}`
        : '',
    currentUserEmail: (state) =>
      state.currentUser
        ? state.currentUser.emailAddress
        : state.username || 'not_specified',
    currentUser: (state) => state.currentUser,
    currentUserContactId: (state) =>
      state.currentUser?.id || state.impersonateContactId || 'not_specified',
    currentUserSimple: (state, getters) => {
      return {
        id: getters.currentUserContactId || '',
        emailAddress: getters.currentUserEmail || '',
        isImpersonating: getters.hasImpersonateContactId,
      }
    },
    isClientGroupOverviewEnabled: (state, getters) =>
      getters.currentUser?.isClientGroupOverviewEnabled,
    msalAccount: (state) => state.account,
    impersonateContactId: (state) => state.impersonateContactId,
    hasImpersonateContactId: (state) => !!state.impersonateContactId,
    getUserStandardBookingDetails: (state) => {
      const standardBookings = state.currentUser?.standardBookings
      if (!standardBookings || standardBookings.length === 0) return null
      return standardBookings[0]
    },
    accessToken: (state) => state.accessToken,
    lastTokenRefresh: (state) => state.lastTokenRefresh,
    isUserLoggedIn: (state) =>
      state.accessToken && (!state.interactionRequired || !!state.account),

    isLoadingAuth: (state) => state.loadingCount > 0,
    isInteractionRequired: (state) => state.interactionRequired,
    auth: (state) => state.auth,
    msalInstance: (state) => msal,
    permissions: (state) => state.permissions,
    redirectToClientOverview: (state) => {
      let clientCount = 0

      if (!state.permissions || state.permissions.length === 0) return false

      for (const group of state.permissions) {
        clientCount += group.clients.length
      }

      return clientCount > 1
    },
    hasMultipleClients: (state, getters) => {
      return getters.redirectToClientOverview
    },
    firstAvailableClient: (state) => {
      return state.permissions[0].clients[0]
    },
    getNode: (state) => (nodeId, level) => {
      return getNodeById(state.permissions, nodeId, level)
    },
    getGroup: (state, getters) => (id) => {
      return getters.getNode(id, LinkType.GROUP)
    },
    getClient: (state, getters) => (id) => {
      return getters.getNode(id, LinkType.CLIENT)
    },
    getLocation: (state, getters) => (id) => {
      return getters.getNode(id, LinkType.LOCATION)
    },
    getClientLocations: (state, getters) => (id) => {
      const client = getters.getClient(id)
      return client.locations
    },
    getNodeHierarchyByIdAndLevel: (state) => (id, linkType) => {
      const flatTree = flattenPermissionsWithLocations(state.permissions)
      if (!flatTree) return null

      switch (linkType) {
        case LinkType.GROUP:
          return flatTree.find((x) => x.groupId === id)
        case LinkType.CLIENT:
          return flatTree.find((x) => x.clientId === id)
        case LinkType.LOCATION:
          return flatTree.find((x) => x.locationId === id)
        default:
          return null
      }
    },
    getFirstClientLocationWithReplaceMePermissions: (state, getters) => (
      clientId
    ) => {
      return getters.getFirstClientLocationWithPermissions(
        clientId,
        PermissionScope.REPLACE_ME
      )
    },
    getAllClients: (state, getters) => {
      return iterateOverPermissionsTree(
        state.permissions,
        LinkType.CLIENT,
        OperationReturnType.LIST
      )
    },
    getAllClientGroups: (state, getters) => {
      return iterateOverPermissionsTree(
        state.permissions,
        LinkType.GROUP,
        OperationReturnType.LIST
      )
    },
    countAllAvailableLocations: (state, getters) => {
      return iterateOverPermissionsTree(
        state.permissions,
        LinkType.LOCATION,
        OperationReturnType.LIST
      ).length
    },
    countAllAvailableBookingLocations: (state, getters) => {
      const list = iterateOverPermissionsTree(
        state.permissions,
        LinkType.LOCATION,
        OperationReturnType.LIST
      )

      return list.filter(
        (locationNode) => locationNode?.access?.permissions?.booking?.view
      ).length
    },
    getTimezoneFromFirstClientGroupLocation: (state, getters) => (
      clientGroupId
    ) => {
      const clientGroup = getters.getGroup(clientGroupId)

      if (
        !clientGroup ||
        !isNonEmptyArray(clientGroup?.clients) ||
        !isNonEmptyArray(clientGroup?.clients[0]?.locations)
      )
        return ''

      return clientGroup?.clients[0]?.locations[0]?.timeZone
    },
    getPermission: (state) => (
      id,
      level,
      scope,
      permission = PermissionModifier.ALL
    ) => {
      return getNodePermission(
        state.permissions,
        id,
        level,
        scope,
        permission // TODO: When more complex permissions required (view, modify, delete) create new perm helpers that use other PermissionModifiers
      )
    },
    hasLocationAccountsPermission: (state, getters) => (id) => {
      return getters.getPermission(
        id,
        LinkType.LOCATION,
        PermissionScope.ACCOUNTS
      )
    },
    hasLocationBookingPermission: (state, getters) => (id) => {
      return getters.getPermission(
        id,
        LinkType.LOCATION,
        PermissionScope.BOOKING
      )
    },
    hasLocationTimesheetPermission: (state, getters) => (id) => {
      return getters.getPermission(
        id,
        LinkType.LOCATION,
        PermissionScope.TIMESHEETS
      )
    },
    hasLocationReplaceMePermission: (state, getters) => (id) => {
      return getters.getPermission(
        id,
        LinkType.LOCATION,
        PermissionScope.REPLACE_ME
      )
    },
    hasClientAccountsPermission: (state, getters) => (id) => {
      return getters.getPermission(
        id,
        LinkType.CLIENT,
        PermissionScope.ACCOUNTS
      )
    },
    hasClientBookingPermission: (state, getters) => (id) => {
      return getters.getPermission(id, LinkType.CLIENT, PermissionScope.BOOKING)
    },
    hasClientTimesheetPermission: (state, getters) => (id) => {
      return getters.getPermission(
        id,
        LinkType.CLIENT,
        PermissionScope.TIMESHEETS
      )
    },
    hasClientReplaceMePermission: (state, getters) => (id) => {
      return getters.getPermission(
        id,
        LinkType.CLIENT,
        PermissionScope.REPLACE_ME
      )
    },
    hasGroupAccountsPermission: (state, getters) => (id) => {
      return getters.getPermission(id, LinkType.GROUP, PermissionScope.ACCOUNTS)
    },
    hasGroupBookingPermission: (state, getters) => (id) => {
      return getters.getPermission(id, LinkType.GROUP, PermissionScope.BOOKING)
    },
    hasGroupTimesheetPermission: (state, getters) => (id) => {
      return getters.getPermission(
        id,
        LinkType.GROUP,
        PermissionScope.TIMESHEETS
      )
    },
    hasGroupReplaceMePermission: (state, getters) => (id) => {
      return getters.getPermission(
        id,
        LinkType.GROUP,
        PermissionScope.REPLACE_ME
      )
    },
    hasPermissionRegardlessOfLevel: (state, getters) => (permissionScope) => {
      return hasPermissionAtAnyLevel(state.permissions, permissionScope)
    },
    flatAccessTree: (state) => {
      return flattenAccessTree(state.permissions)
    },
    getGeneralFiles: (state) => state.generalFiles,
    getFirstClientLocationWithPermissions: (state, getters) => (
      clientId,
      permissionScope
    ) => {
      const client = getters.getClient(clientId)
      if (!client) return null
      if (client.locations.length === 0) return null

      // If client has permission, return first location
      if (getters.getPermission(clientId, LinkType.CLIENT, permissionScope)) {
        return client.locations[0]
      }

      // Otherwise find first client location with permissions
      const firstAuthorisedLocation = client.locations.find(
        (x) => x.access?.permissions?.[permissionScope]
      )
      return firstAuthorisedLocation
    },
    getAllClientLocationsWithPermission: (state, getters) => (
      clientId,
      permissionScope
    ) => {
      const client = getters.getClient(clientId)
      if (!client || client?.locations.length === 0) return false

      // If client has permission, return all locations
      if (getters.getPermission(clientId, LinkType.CLIENT, permissionScope)) {
        return client.locations
      }

      const locations = []
      for (const location of client.locations) {
        if (
          getters.getPermission(
            location.locationId,
            LinkType.LOCATION,
            permissionScope
          )
        )
          locations.push(location)
      }

      return locations
    },
    getAllClientLocationsWithReplaceMePermission: (state, getters) => (
      clientId
    ) => {
      const client = getters.getClient(clientId)
      if (!client || client?.locations.length === 0) return false

      const locations = []
      for (const location of client.locations) {
        if (
          !!location.tags.find((tag) => tag === Tags.REPLACE_ME_ENABLED) &&
          getters.hasLocationReplaceMePermission(location.locationId)
        )
          locations.push(location)
      }

      return locations
    },
    hasPermissionForAtleastOneClientLocation: (state, getters) => (
      clientId,
      permissionScope
    ) => {
      const client = getters.getClient(clientId)
      if (!client || client?.locations.length === 0) return false

      // Check if client has permission
      if (getters.getPermission(clientId, LinkType.CLIENT, permissionScope))
        return true

      // Check if at least one location has the permission
      for (const location of client.locations) {
        if (
          getters.getPermission(
            location.locationId,
            LinkType.LOCATION,
            permissionScope
          )
        )
          return true
      }

      return false
    },
    hasReplaceMePermissionForAtleastOneClientLocation: (state, getters) => (
      clientId
    ) => {
      const client = getters.getClient(clientId)
      if (!client || client?.locations.length === 0) return false
      for (const location of client.locations) {
        if (
          !!location.tags.find((tag) => tag === Tags.REPLACE_ME_ENABLED) &&
          getters.hasLocationReplaceMePermission(location.locationId)
        )
          return true
      }

      return false
    },
    hasCreateBookingPermissionForAtleastOneClientLocation: (state, getters) => (
      clientId
    ) => {
      const hasCreateBookingPermission = getters.getFirstClientLocationWithPermissions(
        clientId,
        PermissionScope.BOOKING
      )

      return !!hasCreateBookingPermission
    },
  },

  actions: {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    init({ dispatch }) {},

    // Login via firebase
    async logIn({ dispatch, getters, commit }, { username, password }) {
      // Clean out localStorage to ensure AAD credentials don't remain
      dispatch('clearStore', null, { root: true })

      // Setting username in the store to assist with logging
      commit('SET_USERNAME', username)

      if (getters.isUserLoggedIn) return dispatch('refreshToken')

      commit('START_LOADING')

      return await firebase
        .auth()
        .signInWithEmailAndPassword(username, password)
        .then(async (response) => {
          if (!response.user)
            return Promise.reject(
              fail([], this.$i18n.t('auth.loginGetProfileFailureErrorText'))
            )
          commit('SET_INTERACTION_REQUIRED', false)
          return success()
        })
        .catch((error) => {
          let message = ''
          if (error.code === 'auth/wrong-password')
            message = this.$i18n.t('auth.loginWrongPasswordErrorText')
          else if (error.code === 'auth/user-not-found')
            message = this.$i18n.t('auth.loginUserNotFoundErrorText')
          else message = error.message

          return fail([], message)
        })
        .finally(() => {
          commit('FINISH_LOADING')
        })
    },

    /**
     * Handles redirect auth from MSAL
     * @param {Object} response Response will be populated on msal login
     */
    async handleRedirect({ dispatch }, response) {
      // Redirect to home after login
      if (response !== null) {
        await dispatch('getUserFromMsalProvider')
        router.push({ name: 'home' })
      }

      // In case multiple accounts exist, you can select
      const currentAccounts = msal.getAllAccounts()
      if (currentAccounts.length > 0) {
        // TODO: Add choose account code here
        await dispatch('getUserFromMsalProvider')
      }
    },

    // Handles already logged in impersonate redirect from login page
    async msalAlreadyLoggedInRedirect(
      { dispatch, commit },
      impersonateContactId
    ) {
      // Attempt to log out of firebase & clear out store in preperation
      try {
        await dispatch('firebaseLogOut', {
          nuke: false,
          showNotifications: false,
          redirect: false,
          setUnauthenticated: false,
        })
      } catch {}

      // Clear out certain values that need to be retrieved from API
      await dispatch('client/clear', null, { root: true })
      commit('FRESH_IMPERSONATE_CLEAR_STORE')

      // Set the new impersonate contact Id
      commit('SET_IMPERSONATE_CONTACT_ID', impersonateContactId)

      // Redirect to dashboard
      router.push({ name: 'home' })
    },

    async clearImpersonateId({ commit }) {
      commit('FRESH_IMPERSONATE_CLEAR_STORE')
    },

    // Logs in the current user.
    async msalLogIn({ dispatch, getters, commit }, impersonateContactId) {
      // Attempt to log out of firebase & clear out store in preperation
      try {
        await dispatch('firebaseLogOut', {
          nuke: true,
          showNotifications: false,
          redirect: false,
          setUnauthenticated: false,
        })
      } catch {}

      commit('SET_IMPERSONATE_CONTACT_ID', impersonateContactId)

      if (getters.isUserLoggedIn) return dispatch('msalRefreshToken')

      const loginRequest = {
        scopes: ['openid'],
      }

      msal.loginRedirect(loginRequest).catch((error) => {
        commit('SET_USER_TO_UNAUTHENTICATED')

        const errorCode = error.errorCode

        const noNotificationReq = ['user_cancelled']

        // Filter through errors that don't require a notifiction
        if (noNotificationReq.some((v) => errorCode.includes(v)))
          return fail(error)

        toast.error('Failed to login as impersonated contact')
        return fail(error)
      })
    },

    // Logs out the current user.
    async msalLogOut(
      { commit, dispatch },
      payload = { redirect: true, nuke: true }
    ) {
      const { redirect, nuke } = payload

      commit('START_LOADING')

      return await msal
        .logout({})
        .then(() => {
          commit('SET_USER_TO_UNAUTHENTICATED')
          // Nuke store
          if (nuke) dispatch('clearStore', null, { root: true })
          if (redirect)
            router.push({ path: `${getLanguageBasedOnBaseURL()}/landing` })

          return success()
        })
        .catch((error) => {
          toast.error(this.$i18n.t('auth.signOutFailureErrorText'))
          return fail(error)
        })
        .finally(() => {
          commit('FINISH_LOADING')
        })
    },

    // Retrieves user account from auth provider
    getUserFromMsalProvider({ commit, dispatch }) {
      if (!msal) return Promise.resolve(null)

      try {
        const myAccounts = msal.getAllAccounts()
        commit('SET_ACCOUNT', myAccounts[0])
      } catch {
        commit('SET_ACCOUNT', null)
      }
    },

    // Validates the current user's token and refreshes it
    // with new data from the API.
    async msalRefreshToken({ dispatch, commit, state }) {
      if (!msal) return Promise.resolve(fail()) // Prevents trying to access auth object before it is initialised
      await dispatch('getUserFromMsalProvider')
      if (!state.account) return Promise.resolve(fail())

      commit('SET_USERNAME', state.account?.username)

      commit('START_LOADING')

      const request = {
        scopes: [config.get('scopes.openId'), config.get('scopes.read')],
        account: state.account,
      }

      let response

      try {
        response = await msal.acquireTokenSilent(request)
        commit('SET_ACCESS_TOKEN', response.accessToken)
        return success()
      } catch (error) {
        console.warn('Silent token acquisition failed. Using interactive mode')
        return await msal
          .acquireTokenPopup(request)
          .then((response) => {
            commit('SET_ACCESS_TOKEN', response.accessToken)
            return success()
          })
          .catch(() => {
            toast.error('Failed to authenticate as impersonated contact')
            return fail()
          })
      } finally {
        commit('FINISH_LOADING')
      }
    },

    // Logs out the current user.
    async logOut({ getters, dispatch }, payload) {
      return dispatch(
        getters.impersonateContactId ? 'msalLogOut' : 'firebaseLogOut',
        payload
      )
    },

    // Logs out the current user.
    async firebaseLogOut(
      { commit, dispatch },
      payload = {
        redirect: true,
        nuke: true,
        showNotifications: true,
        setUnauthenticated: true,
      }
    ) {
      const { redirect, nuke, showNotifications, setUnauthenticated } = payload

      commit('START_LOADING')
      return await firebase
        .auth()
        .signOut()
        .then(() => {
          if (setUnauthenticated) commit('SET_USER_TO_UNAUTHENTICATED')
          // Nuke store
          if (nuke) dispatch('clearStore', null, { root: true })
          if (redirect)
            window.location.href = `/${getLanguageBasedOnBaseURL()}/landing`

          return success()
        })
        .catch((error) => {
          if (showNotifications)
            toast.error(this.$i18n.t('auth.signOutFailureErrorText'))
          return fail(error)
        })
        .finally(() => {
          commit('FINISH_LOADING')
        })
    },

    async refreshToken({ dispatch, getters }, forceRefresh = false) {
      return await dispatch(
        getters.impersonateContactId
          ? 'msalRefreshToken'
          : 'firebaseRefreshToken',
        forceRefresh
      )
    },

    /**
     * Checks freshness of access token and will force refresh the token if token
     * is considered stale
     * @param {*} context Vuex context
     * @param {Boolean} forceRefresh Forces a token refresh
     * @returns Access token
     */
    async getAccessTokenOrRefresh({ dispatch, getters }, forceRefresh = false) {
      const isAccessTokenFresh = isCacheFresh({
        cacheDuration: 30,
        durationUnits: DurationUnits.MINUTE,
        lastUpdated: getters.lastTokenRefresh,
        forceRefresh,
      })

      if (!isAccessTokenFresh) {
        await dispatch('refreshToken', true)
      }

      return getters.accessToken
    },

    // Validates the current user's token and refreshes it using Firebase
    // with new data from the API.
    async firebaseRefreshToken({ commit }, forceRefresh = false) {
      const currentUser = firebase.auth().currentUser
      commit('SET_USERNAME', currentUser?.email)

      return currentUser
        .getIdToken(forceRefresh)
        .then(function(idToken) {
          commit('SET_ACCESS_TOKEN', idToken)
          return Promise.resolve(success())
        })
        .catch(function(error) {
          console.warn(error)

          // Handle error
          commit('SET_USER_TO_UNAUTHENTICATED')
          return Promise.resolve(fail(error))
        })
    },

    async resetPasswordAsync({ commit }, payload) {
      commit('START_LOADING')

      return await firebase
        .auth()
        .sendPasswordResetEmail(payload.email)
        .then(() => {
          toast.success(this.$i18n.t('auth.resetPasswordSuccessText'))
          return success()
        })
        .catch((error) => {
          let message = ''
          if (error.code === 'auth/user-not-found')
            message = this.$i18n.t(
              'auth.resetPasswordAccountDoesNotExistErrorText'
            )
          else message = error.message

          return fail([], message)
        })
        .finally(() => commit('FINISH_LOADING'))
    },

    /**
     * Used to reauthenticate a user before a sensative action (e.g. change password, change email address)
     * This is a security requirement enforced by firebase.
     * Read more: https://firebase.google.com/docs/reference/js/firebase.User#reauthenticatewithcredential
     * @param {String} password
     */
    async reauthenticateWithCredentialsAsync({ commit }, password) {
      commit('START_LOADING')

      const user = firebase.auth().currentUser

      // Prepare auth credentials
      const credentials = firebase.auth.EmailAuthProvider.credential(
        user.email,
        password
      )

      // Use credentials to reauthenticate user
      return await user
        .reauthenticateWithCredential(credentials)
        .then(() => {
          return success()
        })
        .catch(() => {
          toast.error(
            this.$i18n.t('auth.failedToAuthenticateWithCredsErrorText')
          )
          return fail()
        })
        .finally(() => commit('FINISH_LOADING'))
    },

    async changePasswordAsync({ commit }, payload) {
      commit('START_LOADING')

      const user = firebase.auth().currentUser

      return await user
        .updatePassword(payload.newPass)
        .then(() => {
          toast.success(this.$i18n.t('auth.changePasswordSuccessText'))
          return success()
        })
        .catch((error) => {
          return fail(error)
        })
        .finally(() => commit('FINISH_LOADING'))
    },

    /**
     * Loads user profile from ClientLogin API.
     * @param {Boolean} forceRefresh forces refresh of user profile, bypassing the cache
     * @returns
     */
    async getCurrentUserProfile(
      { commit, dispatch, getters },
      forceRefresh = false
    ) {
      // 1. Check cache freshness
      if (
        getters.currentUser &&
        getters.permissions &&
        getters.permissions.length > 0 &&
        isCacheFresh({
          cacheDuration: 2,
          durationUnits: DurationUnits.HOUR,
          lastUpdated: getters.currentUser.lastUpdated,
          forceRefresh,
        })
      )
        return success(getters.currentUser)

      // 2. Load profile from API & cache user profile
      commit('START_LOADING')

      try {
        const response = await this.$api.user.get()
        if (isSuccess(response.status)) {
          commit('SET_CURRENT_USER', response.data.contact)
          commit('SET_USER_PERMISSIONS', response.data.links)

          // After permission tree is set, verify that currently selected client
          // existing within tree
          dispatch('client/validateSelectedClient', null, { root: true })

          commit('SET_USER_GENERALFILES', response.data.generalFiles)

          return success(getters.currentUser)
        }
      } catch (ex) {
        const errorResponse = ErrorResponseFactory(ex, this.$i18n)

        await dispatch(
          'logStoreException',
          new StoreErrorDTO({
            err: ex,
            module: getters.moduleName,
            errorResponse,
          }),
          { root: true }
        )

        return fail(errorResponse)
      } finally {
        commit('FINISH_LOADING')
      }
    },

    clear({ commit }) {
      commit('SET_USER_TO_UNAUTHENTICATED')
    },
  },
}
